import { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { PageHeader, Panel, Pill, Avatar, StatCard } from "@/components/Bits";
import { MagnifyingGlass, Bus, Heart, Sparkle, Baby, Funnel } from "@phosphor-icons/react";

const ENROLMENT_BUCKETS = [
  { key: "all", label: "All", tone: "ink" },
  { key: "preschool", label: "Preschool", tone: "terracotta" },
  { key: "daycare", label: "Daycare", tone: "sage" },
  { key: "preschool_daycare", label: "Both", tone: "mustard" },
  { key: "activity_only", label: "Activity-only", tone: "lavender" },
];

export default function Students() {
  const [students, setStudents] = useState([]);
  const [q, setQ] = useState("");
  const [bucket, setBucket] = useState("all");

  useEffect(() => { api.get("/students").then(r => setStudents(r.data)); }, []);

  const counts = useMemo(() => ({
    total: students.length,
    preschool: students.filter(s => s.enrolment_type === "preschool").length,
    daycare: students.filter(s => s.enrolment_type === "daycare").length,
    both: students.filter(s => s.enrolment_type === "preschool_daycare").length,
    transport: students.filter(s => s.transport_required).length,
    allergies: students.filter(s => (s.medical?.allergies || []).length > 0).length,
  }), [students]);

  const filtered = students.filter(s => {
    if (q && !(s.name?.toLowerCase().includes(q.toLowerCase()) || s.class_name?.toLowerCase().includes(q.toLowerCase()))) return false;
    if (bucket !== "all" && s.enrolment_type !== bucket) return false;
    return true;
  });

  const enrolMeta = (et) => ENROLMENT_BUCKETS.find(b => b.key === et) || ENROLMENT_BUCKETS[1];

  return (
    <div data-testid="students-page">
      <PageHeader eyebrow="Roster" title="Students" subtitle="Every little learner, one warm directory." />

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
        <StatCard label="Total" value={counts.total} icon={Baby} accent="terracotta" />
        <StatCard label="Preschool" value={counts.preschool} icon={Sparkle} accent="terracotta" />
        <StatCard label="Daycare" value={counts.daycare} icon={Sparkle} accent="sage" />
        <StatCard label="Transport" value={counts.transport} icon={Bus} accent="lavender" />
        <StatCard label="Health alerts" value={counts.allergies} icon={Heart} accent="mustard" />
      </div>

      <div className="flex flex-col lg:flex-row lg:items-center gap-3 mb-5">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute top-1/2 -translate-y-1/2 left-3 text-ink-soft" />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search students or class…" className="w-full h-11 pl-10 pr-3 rounded-full bg-white border border-border" data-testid="students-search" />
        </div>
        <div className="flex items-center gap-2 overflow-x-auto">
          <Funnel size={16} className="text-ink-soft shrink-0" />
          {ENROLMENT_BUCKETS.map(b => (
            <button key={b.key} onClick={() => setBucket(b.key)} data-testid={`bucket-${b.key}`}
              className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap ${bucket === b.key ? "bg-ink text-white" : "bg-white border border-border"}`}>
              {b.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4" data-testid="students-grid">
        {filtered.map(s => {
          const em = enrolMeta(s.enrolment_type);
          return (
            <Link to={`/app/students/${s.student_id}`} key={s.student_id} className="bg-white rounded-3xl border border-border p-5 hover:shadow-hoverlift transition block" data-testid={`student-card-${s.student_id}`}>
              <div className="flex items-center gap-3">
                <Avatar name={s.name} size={48} />
                <div className="min-w-0">
                  <div className="font-heading font-bold truncate">{s.name}</div>
                  <div className="text-xs text-ink-soft">{s.age_group} · {s.class_name}</div>
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1.5">
                <Pill tone={em.tone}>{em.label}</Pill>
                {s.transport_required && <Pill tone="lavender"><Bus size={10} weight="bold" className="mr-1" /> Transport</Pill>}
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                <div className="bg-paper-alt rounded-xl p-2"><div className="text-ink-soft">Blood</div><div className="font-semibold">{s.blood_group || "—"}</div></div>
                <div className="bg-paper-alt rounded-xl p-2"><div className="text-ink-soft">Status</div><div className="font-semibold capitalize">{s.status}</div></div>
              </div>
              <div className="mt-3 flex flex-wrap gap-1">
                {(s.medical?.allergies || []).map(a => <Pill key={a} tone="danger">{a}</Pill>)}
              </div>
            </Link>
          );
        })}
      </div>
    </div>
  );
}
